import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, Typography, message } from 'antd';
import { makeStyles } from '@material-ui/core';
import { createUniqueGroupId } from '../../../utilities';
import { adminColors, adminModal } from './adminStyles';

const { Option } = Select;
const { Text } = Typography;

const useStyles = makeStyles((theme) => ({
  modal: adminModal(theme),
  playerSection: {
    padding: '16px 0 4px',
    '&:first-child': {
      paddingTop: 0,
    },
    '& + &': {
      borderTop: `1px solid ${adminColors.border}`,
    },
  },
  playerTitle: {
    display: 'block',
    marginBottom: 8,
  },
  formItem: {
    marginBottom: 12,
  },
}));

const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

export default function PlayerGroupModal({ visible, onCancel, onSuccess, mode = 'add', group }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [emailErrors, setEmailErrors] = useState({});
  const styles = useStyles();

  const isEdit = mode === 'edit';

  // In edit mode the number of player forms follows the group; add mode is always 4.
  const playerCount = isEdit && group ? group.length : 4;
  const playerNumbers = Array.from({ length: playerCount }, (_, i) => i + 1);

  // Seed the form whenever the modal opens (or the target group changes).
  useEffect(() => {
    if (!visible) return;

    if (isEdit && group && group.length) {
      const seeded = {};
      group.forEach((player, index) => {
        seeded[`player${index + 1}_name`] = player.name;
        seeded[`player${index + 1}_email`] = player.email;
        seeded[`player${index + 1}_shirtSize`] = player.shirtSize;
      });
      form.setFieldsValue(seeded);
      setFormValues(seeded);
    } else {
      form.resetFields();
      setFormValues({});
    }
    setEmailErrors({});
  }, [visible, isEdit, group, form]);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email blur validation
  const handleEmailBlur = (playerNumber, email) => {
    const fieldName = `player${playerNumber}_email`;
    if (email && !validateEmail(email)) {
      setEmailErrors(prev => ({
        ...prev,
        [fieldName]: 'Please enter a valid email address'
      }));
    } else {
      setEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handle form value changes
  const handleFormChange = (changedValues, allValues) => {
    setFormValues(allValues);
  };

  // Check if form is complete and valid
  const isFormComplete = () => {
    const requiredFields = playerNumbers.flatMap(n => [
      `player${n}_name`, `player${n}_email`, `player${n}_shirtSize`
    ]);

    // Check if all required fields are filled
    const allFieldsFilled = requiredFields.every(field =>
      formValues[field] && formValues[field].toString().trim() !== ''
    );

    // Check if there are no email errors
    const noEmailErrors = Object.keys(emailErrors).length === 0;

    // Check if all emails are valid
    const emailFields = playerNumbers.map(n => `player${n}_email`);
    const allEmailsValid = emailFields.every(field =>
      !formValues[field] || validateEmail(formValues[field])
    );

    return allFieldsFilled && noEmailErrors && allEmailsValid;
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (isEdit) {
        // Preserve _id (and implicitly groupId/paymentId) for each existing player.
        const groupId = group[0].groupId;
        const players = group.map((player, index) => ({
          _id: player._id,
          name: values[`player${index + 1}_name`],
          email: values[`player${index + 1}_email`],
          shirtSize: values[`player${index + 1}_shirtSize`],
        }));

        const response = await fetch(`/api/players/group/${groupId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ players }),
        });

        if (!response.ok) {
          throw new Error('Failed to update player group');
        }

        message.success('Player group updated successfully!');
        onSuccess();
      } else {
        const groupId = createUniqueGroupId();

        // Create array of 4 players with the generated groupId
        const players = playerNumbers.map(n => ({
          name: values[`player${n}_name`],
          email: values[`player${n}_email`],
          shirtSize: values[`player${n}_shirtSize`],
          groupId,
          paymentId: 'MANUAL_ENTRY', // Placeholder for manually added groups
        }));

        const response = await fetch('/api/players/group', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ players }),
        });

        if (!response.ok) {
          throw new Error('Failed to create player group');
        }

        message.success('Player group added successfully!');
        form.resetFields();
        onSuccess();
      }
    } catch (error) {
      message.error(`Failed to ${isEdit ? 'update' : 'add'} player group. Please try again.`);
      console.error(`Error ${isEdit ? 'updating' : 'adding'} player group:`, error);
    } finally {
      setLoading(false);
    }
  };

  const renderPlayerForm = (playerNumber) => (
    <div key={playerNumber} className={styles.playerSection}>
      <Text strong className={styles.playerTitle}>Player {playerNumber}</Text>
      <Row gutter={12}>
        <Col xs={24} md={9}>
          <Form.Item
            name={`player${playerNumber}_name`}
            rules={[{ required: true, message: 'Name is required' }]}
            className={styles.formItem}
          >
            <Input placeholder="Full name" aria-label={`Player ${playerNumber} full name`} />
          </Form.Item>
        </Col>
        <Col xs={24} md={10}>
          <Form.Item
            name={`player${playerNumber}_email`}
            rules={[
              { required: true, message: 'Email is required' }
            ]}
            validateStatus={emailErrors[`player${playerNumber}_email`] ? 'error' : ''}
            help={emailErrors[`player${playerNumber}_email`]}
            className={styles.formItem}
          >
            <Input
              type="email"
              placeholder="Email"
              aria-label={`Player ${playerNumber} email`}
              onBlur={(e) => handleEmailBlur(playerNumber, e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={5}>
          <Form.Item
            name={`player${playerNumber}_shirtSize`}
            rules={[{ required: true, message: 'Shirt size is required' }]}
            className={styles.formItem}
          >
            <Select placeholder="Shirt size" aria-label={`Player ${playerNumber} shirt size`}>
              {SHIRT_SIZES.map(size => (
                <Option key={size} value={size}>{size}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  return (
    <Modal
      title={isEdit ? 'Edit player group' : 'Add player group'}
      visible={visible}
      onCancel={onCancel}
      width={720}
      centered
      destroyOnClose
      className={styles.modal}
      // The form wraps the whole dialog so the footer's submit button (and Enter) submit it.
      modalRender={(dialog) => (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          scrollToFirstError
        >
          {dialog}
        </Form>
      )}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={!isFormComplete()}
        >
          {isEdit ? 'Save changes' : 'Add group'}
        </Button>,
      ]}
    >
      {playerNumbers.map(playerNumber => renderPlayerForm(playerNumber))}
    </Modal>
  );
}
